import { useSelector } from 'react-redux';

import { getCurrency, getLanguage } from '@travel/i18n';
import useDeviceType from '@travel/traveler-core/hooks/useDeviceType';
import { IconTextList } from '@travel/ui';
import { cx, isEmptyArray, isNotEmptyArray } from '@travel/utils';

import GallerySlider from 'components/GallerySlider';
import TaxAndFeeSection from 'components/TaxAndFeeSection';
import { mappedIconTextListData } from 'pages/ProviderInformationPage/helpers';

import { getIsDirectContractType } from 'store/providerInformation/selectors';
import { getPlanDetail } from 'store/providerRatePlan/selectors';

import type { Plan } from 'ProviderRatePlan-Types';

import CancellationPolicy from '../CancellationPolicy';
import PlanDetailHeaderMain from './PlanDetailHeaderMain';
import type { Props as PlanDetailHeaderProps } from './PlanItem';

import styles from './itemDetails.module.scss';

type Props = {
  className?: string;
  onReserve: () => void;
} & Omit<PlanDetailHeaderProps, 'type'>;

function PlanDetails(props: Props) {
  const deviceType = useDeviceType();
  const isMobile = deviceType === 'sp';
  const isDirectContract = useSelector(getIsDirectContractType);

  const { itemId, planId, searchParam, isOpenDetail } = props;
  const plan: Plan = useSelector(
    getPlanDetail(props.itemId, props.planId, isDirectContract, props.ratePlanId),
  );
  const language = useSelector(getLanguage);
  const currency = useSelector(getCurrency);

  const isDatedSearch = searchParam?.startDate || searchParam?.endDate;
  const onPage = isDatedSearch ? 'providerInfoDated' : 'providerInfoUndated';

  const { facilities, cancellationPenalties, price, taxesAndFees, media } = plan;

  return (
    <div
      className={cx(styles.dialogContainer, !isDatedSearch && styles.calendarDialogHeight)}
      data-testid="planDetail-dialogContent"
    >
      {isDirectContract && (
        <GallerySlider
          onPage={onPage}
          className={cx(styles.mediaSlider, styles.mediaSize)}
          mediaClassName={styles.mediaSize}
          videoClassName={styles.mediaVideo}
          isShowGalleryOnClick={isMobile}
          media={media}
          mediaSizes={isMobile ? '100vw' : '30vw'}
        />
      )}
      {/* details */}
      <div className={styles.detailInfoWrapper}>
        <div className={styles.detailInfoContainer}>
          <PlanDetailHeaderMain
            onPage={onPage}
            itemId={itemId}
            planId={planId}
            ratePlanId={props.ratePlanId}
            appliedSalesPromotionIds={props.appliedSalesPromotionIds}
            perRoomPriceBeforeDiscount={props.perRoomPriceBeforeDiscount}
            perRoomPriceAfterDiscount={props.perRoomPriceAfterDiscount}
            providerId={props.providerId}
            searchParam={props.searchParam}
            onSelectDate={props.onSelectDate}
            query={props.query}
            onReserve={props.onReserve}
          />
        </div>

        {/* facilities */}
        {isNotEmptyArray(facilities) &&
          facilities.map(({ group, features }) => {
            const filteredFeatures = mappedIconTextListData(features, styles.featureIcon);
            if (isEmptyArray(filteredFeatures)) {
              return null;
            }

            return (
              <div
                className={styles.detailInfoContainer}
                data-testid="planDetail-dialogContent-facilities"
                key={group}
              >
                <div className={styles.containerTitle}>{group}</div>
                <IconTextList
                  className={styles.featureItemContainer}
                  iconTextClassName={styles.featureItem}
                  iconTextSpacing={4}
                  spaceBetween={0}
                  items={filteredFeatures}
                />
              </div>
            );
          })}

        {/* cancellation policy */}
        {isNotEmptyArray(cancellationPenalties) && (
          <CancellationPolicy
            wrapperClassName={styles.detailInfoContainer}
            titleClassName={styles.containerTitle}
            cancellationPenalties={cancellationPenalties}
            currency={currency}
            language={language}
            price={price}
            cancellationRemarks={plan.cancellationRemarks}
            isDirectContract={isDirectContract}
            isOpenCancellationPolicyPopup={isOpenDetail && !isDirectContract}
          />
        )}

        {/* tax / fee */}
        <TaxAndFeeSection
          className={styles.detailInfoContainer}
          titleClassName={cx(styles.containerTitle, styles.taxFee)}
          excludedFeeRemarkClassName={styles.excludedFeeRemark}
          includeTaxes={taxesAndFees?.included}
          excludeTaxes={taxesAndFees?.excluded}
          pageName="provider_information"
          shouldShowIndirectProviderDisclaimer={!isDirectContract}
          isIndirectProvider={!isDirectContract}
        />
      </div>
    </div>
  );
}

export default PlanDetails;
